import React from 'react';
import { StyleSheet, View, type ViewProps, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

import type { PaletteProps } from '../../Modal.types';

export const ModalContent = (props: ModalContentProps) => {
  const { children, palette, shouldFitToFill, withSafeAreaView, style, ...restProps } = props;
  const { currentBreakpoint } = useResponsive();
  const insets = useSafeAreaInsets();

  const responsiveStyles = currentBreakpoint.isXS ? styles.contentMobile : styles.contentTabletUp;
  const sizeBasedStyles = shouldFitToFill ? styles.contentFitToFill : responsiveStyles;

  const paletteSpecificStyles = palette ? modalPaletteStyles[palette] : undefined;

  // NOTE: Only content that reaches the bottom edge of the screen needs the inset by default.
  const shouldReserveSafeArea = withSafeAreaView ?? (currentBreakpoint.isXS || shouldFitToFill);

  const safeAreaPaddingStyle = useStyle(() => {
    return shouldReserveSafeArea ? { paddingBottom: insets.bottom } : undefined;
  }, [shouldReserveSafeArea, insets.bottom]);

  return (
    <View
      style={[styles.content, sizeBasedStyles, paletteSpecificStyles, safeAreaPaddingStyle, style]}
      {...restProps}
    >
      {children}
    </View>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    maxHeight: '100%',
    overflow: 'hidden',
  },
  contentMobile: {
    ...theme.elevations['2'],
    borderTopRightRadius: theme.radius.large,
    borderTopLeftRadius: theme.radius.large,
  },
  contentTabletUp: {
    ...theme.elevations['3'],
    borderRadius: theme.radius.large,
  },
  contentFitToFill: {
    flexGrow: 1,
  },
  paletteSecondary: {
    backgroundColor: theme.colors.SPINACH,
  },
  paletteLime: {
    backgroundColor: theme.colors.LIME,
  },
  paletteForest: {
    backgroundColor: theme.colors.FOREST,
  },
  paletteDarkKale: {
    backgroundColor: theme.colors.DARK_KALE,
  },
});

const modalPaletteStyles: PaletteStyles = {
  primary: undefined,
  neutral: undefined,
  muted: undefined,
  secondary: styles.paletteSecondary,
  lime: styles.paletteLime,
  forest: styles.paletteForest,
  'dark-kale': styles.paletteDarkKale,
};

// ─── Types ─────────────────────────────────────────────────────────────

type ModalContentProps = ViewProps &
  PaletteProps &
  Readonly<{
    /**
     * Determines whether modal content should flex grow.
     */
    shouldFitToFill: boolean;

    /**
     * Determines whether modal content should reserve space for the device safe area.
     *
     * Defaults to `true` on compact screens and for full screen modals, and to
     * `false` for smaller modals on larger screens, which float clear of the edge.
     */
    withSafeAreaView?: boolean;
  }>;

type PaletteStyles = Record<NonNullable<PaletteProps['palette']>, ViewStyle | undefined>;
